import { useState } from "react";
import { ChevronDown, ChevronUp, Building2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CHANGE_REQUEST_OPTIONS, AVAILABLE_HOURS } from "./constants";
import type { TicketRoom } from "./ticket-form.types";

function getTomorrowDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0]!;
}

interface TicketRoomCardProps {
  room: TicketRoom;
  index: number;
  defaultExpanded?: boolean;
  showVisitScheduling?: boolean;
  onFieldChange: (
    index: number,
    field: keyof TicketRoom,
    value: string | number,
  ) => void;
}

export function TicketRoomCard({
  room,
  index,
  defaultExpanded = true,
  showVisitScheduling = false,
  onFieldChange,
}: TicketRoomCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-200"
      style={{
        borderLeft: "3px solid #FFD900",
        border: "1px solid #E8E6E3",
        borderLeftWidth: "3px",
        borderLeftColor: "#FFD900",
        background: "white",
      }}
    >
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        onClick={() => setExpanded(!expanded)}
        style={{ background: expanded ? "#FDFBF7" : "white" }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Building2
            className="w-4 h-4 flex-shrink-0"
            style={{ color: "#78736D" }}
          />
          <div className="min-w-0">
            <p
              className="text-sm font-semibold"
              style={{
                color: "#1A1814",
                fontFamily: "'Space Grotesk', system-ui, sans-serif",
              }}
            >
              {room.roomName}
            </p>
            <p className="text-xs" style={{ color: "#78736D" }}>
              {room.spaceName}
              {room.address ? ` — ${room.address}` : ""}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp
            className="w-4 h-4 flex-shrink-0"
            style={{ color: "#78736D" }}
          />
        ) : (
          <ChevronDown
            className="w-4 h-4 flex-shrink-0"
            style={{ color: "#78736D" }}
          />
        )}
      </button>

      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: expanded ? "600px" : "0px",
          opacity: expanded ? 1 : 0,
        }}
      >
        <div
          className="px-4 pb-4 pt-2 flex flex-col gap-3"
          style={{ borderTop: "1px solid #E8E6E3" }}
        >
          <div>
            <label
              className="block text-xs font-medium mb-1.5 uppercase tracking-wider"
              style={{ color: "#78736D", letterSpacing: "0.06em" }}
            >
              Solicitação
            </label>
            <Select
              value={room.changeRequest ?? ""}
              onValueChange={(val) =>
                onFieldChange(index, "changeRequest", val)
              }
            >
              <SelectTrigger
                className="h-9 rounded-lg text-sm"
                style={{ borderColor: "#E8E6E3", color: "#43403D" }}
              >
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                {CHANGE_REQUEST_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label
              className="block text-xs font-medium mb-1.5 uppercase tracking-wider"
              style={{ color: "#78736D", letterSpacing: "0.06em" }}
            >
              Observações da sala
            </label>
            <Textarea
              className="min-h-[60px] rounded-lg text-sm resize-none"
              style={{ borderColor: "#E8E6E3", color: "#43403D" }}
              placeholder="Detalhes adicionais sobre esta sala..."
              value={room.roomObservations ?? ""}
              onChange={(e) =>
                onFieldChange(index, "roomObservations", e.target.value)
              }
            />
          </div>

          {showVisitScheduling && (
            <>
              <div>
                <label
                  className="block text-xs font-medium mb-1.5 uppercase tracking-wider"
                  style={{ color: "#78736D", letterSpacing: "0.06em" }}
                >
                  Data da visita
                </label>
                <input
                  type="date"
                  min={getTomorrowDate()}
                  value={room.requestedVisitDate ?? ""}
                  onChange={(e) =>
                    onFieldChange(index, "requestedVisitDate", e.target.value)
                  }
                  onClick={(e) => e.currentTarget.showPicker?.()}
                  className="w-full h-10 px-3 rounded-xl text-sm outline-none transition-colors cursor-pointer"
                  style={{ border: "1px solid #E8E6E3", color: "#43403D" }}
                  onFocus={(e) => (e.target.style.borderColor = "#FFD900")}
                  onBlur={(e) => (e.target.style.borderColor = "#E8E6E3")}
                />
              </div>

              {room.requestedVisitDate && (
                <div>
                  <label
                    className="block text-xs font-medium mb-1.5 uppercase tracking-wider"
                    style={{ color: "#78736D", letterSpacing: "0.06em" }}
                  >
                    Horário da visita
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {AVAILABLE_HOURS.map((hour) => {
                      const isSelected = room.requestedVisitTime === hour;
                      return (
                        <button
                          key={hour}
                          type="button"
                          onClick={() =>
                            onFieldChange(index, "requestedVisitTime", hour)
                          }
                          className="px-3 py-1.5 rounded-lg text-sm transition-all"
                          style={{
                            border: isSelected
                              ? "1.5px solid #FFD900"
                              : "1px solid #E8E6E3",
                            background: isSelected ? "#FFD900" : "white",
                            color: "#1A1814",
                            fontWeight: isSelected ? 600 : 500,
                            cursor: "pointer",
                          }}
                        >
                          {hour}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
